import { Trash } from '@/widget/Icon';
import { useTheme } from '@/components/ThemeProvider';
import { cn } from '@/lib/utils';
import { Search, X as Close } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useSearchHotToken } from './hook/useSearchHotToken';
import Token from './Token';
import { useSearchToken } from './hook/useSearchToken';
import { useNavigate } from 'react-router-dom';
import { useAppEventBusContext } from '@/context/eventBusContext';
import { SubscribeEvent } from '@/interface/event';
import NoData from '@/widget/NoData';
import { t } from 'i18next';
import TokenAvatar from '@/widget/TokenAvatar';
import { useAppStore } from '@/store';
import { chain } from 'lodash';

interface IHistory {
  address: string;
  symbol: string;
  logo: string;
}

export default function SearchInput() {
  const {
    fwClass,
    chainInfo: { path },
  } = useAppStore();
  const { theme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [history, setHistory] = useState<IHistory[]>([]);
  const navigate = useNavigate();
  const { subscribe } = useAppEventBusContext();
  const MAX = 5;

  const { tokenList } = useSearchHotToken(isOpen);
  const { keyword, searchTokenList, onChange, setKeyword } = useSearchToken();

  useEffect(() => {
    const unsubscribe = subscribe(SubscribeEvent.SHOW_SEARCH_SHEET, (state: boolean) => {
      if (state) {
        setIsOpen(state);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    setHistory([]);
  }, [chain]);

  const onSelect = useCallback(
    (e: IHistory) => {
      const index = history.findIndex((item) => item.address === e.address);
      let newHistory: IHistory[] = [];
      if (index == -1) {
        newHistory = [{ address: e.address, symbol: e.symbol, logo: e.logo }, ...history];
      } else {
        newHistory = [...history];
        const [removedItem] = newHistory.splice(index, 1);
        newHistory.unshift(removedItem);
        setHistory(newHistory);
      }
      if (newHistory.length > MAX) {
        newHistory = newHistory.slice(0, MAX);
      }
      setHistory(newHistory);
      navigate(`/${path}/trade/${e.address}`);
      setIsOpen(false);
    },
    [history, path],
  );

  const onClose = () => {
    setIsOpen(false);
    setKeyword('');
  };

  return (
    <Sheet open={isOpen} onOpenChange={() => setIsOpen(true)}>
      <SheetTrigger asChild>
        <div className="flex items-center justify-center">
          <Search className="!w-[20px] !h-[20px]" />
        </div>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        hideCloseIcon
        className="w-full overflow-y-auto p-[16px] top-[100px] border-t-[1px] border-t-borderColor outline-none shadow-none"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onOpenChange={() => setIsOpen(false)}
      >
        <SheetTitle className="hidden" />
        <SheetDescription className="hidden" />
        <div className="relative flex flex-col">
          <div className="flex items-center justify-between mb-[16px]">
            <span className={cn('text-[24px]', fwClass)}>{t('Header.search')}</span>
            <Close onClick={onClose} />
          </div>
          <div className="relative mb-[12px]">
            <Search className="absolute left-2 top-[50%] translate-y-[-50%] h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-8 pr-[30px] rounded-[8px] text-[14px] h-[36px] bg-[#F4F4F5CC] dark:bg-grayColor shadow-none outline-none"
              placeholder={t('Header.enterContractToken')}
              value={keyword}
              onChange={onChange}
            />
            {keyword.length > 0 && (
              <img
                src={theme == 'light' ? '/icon/input-close-light.svg' : '/icon/input-close-dark.svg'}
                className="size-[18px] absolute right-[10px] top-[50%] translate-y-[-50%]"
                onClick={() => setKeyword('')}
              />
            )}
          </div>
          {history.length > 0 ? (
            <div className="mb-[24px]">
              <div className="flex justify-between mb-[8px]">
                <span className=" text-[16px]">{t('Header.searchHistory')}</span>
                <Trash className="w-[18px] h-[18px]" theme={theme} onClick={() => setHistory([])} />
              </div>
              <div className="flex flex-wrap gap-[10px]">
                {history.map((token) => {
                  return (
                    <div
                      key={token.address}
                      className="h-[34px] dark:bg-secondBgColor text-secondaryTextColor p-[2px] pr-[8px] flex items-center gap-x-[6px]"
                      onClick={() => onSelect(token)}
                    >
                      <TokenAvatar logo={token.logo} address={token.address} className="w-[24px] h-[24px]" />
                      <span className="text-[14px] text-secondaryTextColor">{token.symbol}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div />
          )}
          <div className="mb-[12px] flex items-center gap-x-[6px]">
            <span className="text-[16px]">{t('Header.dayTrending')}</span>
            <img className="w-[20px] h-[20px]" src="/icon/hot-active.svg" />
          </div>
          <div className="flex flex-col gap-y-[20px]">
            {keyword.length > 0 ? (
              searchTokenList.length > 0 ? (
                searchTokenList.map((item, index) => <Token key={index} token={item} onSelect={onSelect} />)
              ) : (
                <NoData />
              )
            ) : tokenList.length > 0 ? (
              tokenList.map((item, index) => <Token key={index} token={item} onSelect={onSelect} />)
            ) : (
              <NoData />
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
